/**
 * scenarioParamDefsFields.js
 *
 * DB 표준 시나리오(param_defs + overrides) 공용 파라미터 편집기 — 전 모듈(RP/IP/MP) 공유.
 *
 * 변형(base/alt1/alt2)별로:
 *   - base : 무변경 기준선(현행 유지) — 읽기 전용 안내.
 *   - alt  : 적용 범위(전체/거점/품목) 토글 + 파라미터 절대값 편집(overrides).
 * 대상 거점/품목 후보는 scenario.problem_targets(런타임 실데이터 문제 대상)에서 주입된다.
 * 적용 범위·값은 변형마다 독립이며 같은 파라미터라도 alt1/alt2 값이 다를 수 있다.
 *
 * RpScenarioParamFields(AiRpScenarioPlanning.js)에서 일반화·추출. RP·IP/MP 공용.
 * props: { scenario, accent, disabled, highlights, onParamChange, onScenarioChange }
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Checkbox, Stack, Typography } from "@mui/material";
import { FONT } from "@plannel/components/ai/typographyTokens";
import AiSegmentedControl from "./AiSegmentedControl";
import { hasEffectiveValue, paramRowSx, paramGroupHeaderSx, paramGroupRuleSx,
         PARAM_ROW_RADIUS, PARAM_CARD_RADIUS, PARAM_ROW_PAD_X,
         PARAM_NUM_INPUT_W, PARAM_SELECT_W, isInlineEditable,
         PARAM_ROW_MIN_H, PARAM_TOGGLE_SX } from "./scenarioParamUtil";
import { fmtParamValue, baselineCurrentText, currentPrefixFor, useEditEndGuard,
         ParamValueCells, ParamRevertButton } from "./scenarioParamCells";
import ScenarioParamAddPicker from "./ScenarioParamAddPicker";
import { C } from "@plannel/components/ai/aiTokens";
import { fieldText } from "./aiFieldText";

// 시나리오 단위 적용 범위(scope) 옵션 — 모듈별 구성.
// RP/MP : 전체/거점/품목군(신규 작성 기준. item 은 과거 저장 시나리오 표시용 레거시).
// IP : 전체/거점/ABC-XYZ 분류(품목 단위 정책 없음).
// label/unitLabel 은 i18n 키 — 소비처에서 t() 로 해석(모듈 스코프 상수라 t 미접근).
const SCOPE_DEF = {
  loc:       { value: "loc",       label: "scopeLocLabel",      unitLabel: "scopeLocLabel",        cdKey: "loc_cd",      nameKey: "loc_name",  candKey: "locations",   icon: "" },
  item:      { value: "item",      label: "scopeItemLabel",     unitLabel: "scopeItemLabel",       cdKey: "item_cd",     nameKey: "item_name", candKey: "items",       icon: "" }, // 레거시 해석용(신규 RP_SCOPE_OPTIONS 에서는 미노출)
  item_grp:  { value: "item_grp",  label: "scopeItemGrpLabel",  unitLabel: "scopeItemGrpLabel",    cdKey: "item_grp_cd", nameKey: "item_grp_name", candKey: "item_groups", icon: "" },
  inv_class: { value: "inv_class", label: "scopeInvClassLabel", unitLabel: "scopeClassUnitLabel",  cdKey: "inv_class_cd", nameKey: null,       candKey: "inv_classes", icon: "" },
  all:       { value: "all",       label: "all",                unitLabel: "all",                  cdKey: null,          nameKey: null,        candKey: null,          icon: "" },
};
const RP_SCOPE_OPTIONS = ["loc", "item_grp", "all"].map(k => SCOPE_DEF[k]);
const IP_SCOPE_OPTIONS = ["loc", "inv_class", "all"].map(k => SCOPE_DEF[k]);

// 전체(all) 적용 그리드에서 반 칸(좌우 2열)으로 배치할 파라미터.
// 그 외는 기본 한 행 전체(full-width) — 반영 구간(실적/수요계획)만 좌우로 짝 지어 배치.
const HALF_WIDTH_PARAMS = new Set(["actualPeriodVal", "demandPeriodVal"]);

// 버전 전역(version-scoped) 파라미터 — 거점/ABC-XYZ 분류 단위가 아니라 분류(classification)
// 버전 단위로 일괄 적용된다(백엔드 _extract_eval_period_override 가 모든 override 행에서 머지).
// → 적용 단위 토글과 무관하게 최상단에서 한 번만 설정한다(대상 카드/행에는 미노출).
// 판정 기준은 백엔드 param_defs 의 scope 필드(단일 진실 소스 = meta_catalog PARAM_CATALOG).
export const isVersionParamDef = (d) => d?.scope === "version";
// scope 를 싣지 않던 시절의 payload(캐시·시드 등) 폴백 — 신규 응답에는 항상 scope 가 있다.
const LEGACY_VERSION_KEYS = new Set(["actualPeriodVal", "demandPeriodVal"]);
export const versionParamKeys = (paramDefs) => {
  const scoped = (paramDefs || []).filter(d => d?.scope);
  return scoped.length
    ? new Set(scoped.filter(isVersionParamDef).map(d => d.param_key))
    : LEGACY_VERSION_KEYS;
};

export default function ScenarioParamDefsFields({
  scenario, accent, disabled, highlights, onParamChange, onScenarioChange,
  view = "all", onStats,
}) {
  const { t } = useTranslation();

  // 기준선 현재값 표시 — current_value(원시) + 단위(unit_key 다국어) 앞에 baseline_kind 접두어(현행/평균).
  //   파라미터명은 행 왼쪽 라벨이 이미 말하므로 재출력하지 않는다(무값 시 레거시 current_display 폴백).
  const baselineValue = (def) => {
    const cv = def?.current_value;
    if (cv == null || cv === "") return null;
    return def.type === "select"
      ? (() => { const o = (def.options || []).find(x => String(x.value) === String(cv)); return o ? t(o.label_key || o.label) : String(cv); })()
      : `${Number.isFinite(Number(cv)) ? Number(cv) : cv}${def.unit_key ? t(def.unit_key) : (def.unit || "")}`;
  };
  const fmtBaselineCurrent = (def) => {
    const valPart = baselineValue(def);
    if (valPart == null) return def?.current_display || null;
    return baselineCurrentText(def, valPart, t);
  };
  // base 카드는 헤더가 이미 '현행 유지'라고 말한다 — 행마다 '현행'을 되풀이하지 않고 값만. '평균'은 값의 성격이라 남긴다.
  const fmtBaseCardCurrent = (def) => {
    const valPart = baselineValue(def);
    if (valPart == null) return def?.current_display || null;
    if (def.baseline_kind === "avg") return t("paramAvgValueLabel", { value: valPart });
    if (def.baseline_kind === "current") return valPart;
    return baselineCurrentText(def, valPart, t);   // custom·구버전 응답
  };

  const inputSx = {
    width: "100%", p: "4px 8px", border: `0.5px solid ${accent}60`,
    borderRadius: 1, fontSize: FONT.BODY, fontFamily: "inherit", color: C.text1,
    bgcolor: C.bg2, outline: "none", boxSizing: "border-box",
    "&:focus": { borderColor: accent },
    "&:disabled": { bgcolor: C.bg3, color: C.text3, cursor: "not-allowed", borderColor: C.border },
  };

  // AI Agent set_param 변경 강조 (공통 키 규칙: `${role}:${paramKey}`)
  const hotWrap = (paramKey, node) => {
    const hlTs = highlights?.[`${scenario.role}:${paramKey}`];
    if (hlTs == null) return <Box key={paramKey}>{node}</Box>;
    return (
      <Box key={paramKey}
        sx={{ position: "relative", borderRadius: PARAM_ROW_RADIUS, p: "8px 9px", mx: "-9px",
              bgcolor: `${C.acc}10`, border: `1px solid ${C.acc}55`,
              boxShadow: `0 0 0 3px ${C.acc}12` }}>
        <Box sx={{ position: "absolute", top: -7, right: 7, px: "5px", height: 14,
                   borderRadius: PARAM_ROW_RADIUS, bgcolor: C.acc, color: "#fff", fontSize: FONT.MICRO,
                   fontWeight: 700, display: "flex", alignItems: "center" }}>{t("aiChangedBadge")}</Box>
        {node}
      </Box>
    );
  };

  // 파라미터 입력 1개 렌더 — 전역 params / override.params 공용 (value·onChange 주입)
  // hideLabel: 아코디언 헤더가 라벨을 담당할 때 본문 라벨 생략.
  const renderDef = (def, value, onChange, showCurrent = true, currentText = undefined, hideLabel = false) => {
    const curLine = currentText !== undefined ? currentText : (showCurrent ? fmtBaselineCurrent(def) : null);
    return (
    <Box key={def.param_key}>
      {!hideLabel && (
        <Typography sx={{ fontSize: FONT.BODY, color: C.text3, mb: 0.3 }}>{t(def.label_key || def.label)}</Typography>
      )}
      {def.type === "select" ? (
        /* 🔴 값이 없으면 빈 값을 그대로 그린다 [Q-064]. 예전 폴백 `value ?? def.options?.[0]?.value`
           는 **고르지도 않은 첫 옵션을 실제 값처럼** 보여 줬다 — onChange 가 안 불려 params 에는
           키가 없으므로 엔진에는 가지도 않는 값이다("보는 것 ≠ 도는 것"). [D-043] 이 "틀린 현재값보다
           빈칸이 낫다"며 baseline 을 비웠는데 이 한 줄이 그 빈칸을 도로 채우고 있었다.
           빈 option 은 **항상** 둔다 — ⑴ 값을 지워 미설정으로 되돌리는 유일한 수단이고
           (레거시 분기에는 ParamRevertButton 이 없다), ⑵ 표시값과 DOM value 가 같아 같은 항목을
           다시 골라도 change 가 안 나던 함정도 함께 사라진다. ""는 hasEffectiveValue 가 걸러
           pruneParamsForEngine 에서 드롭되므로 전송 페이로드는 달라지지 않는다. */
        <Box component="select" value={value ?? ""} disabled={disabled}
          onChange={e => onChange(def.param_key, e.target.value)}
          sx={{ ...inputSx, ...PARAM_SELECT_W, cursor: disabled ? "not-allowed" : "pointer" }}>
          <option value="">—</option>
          {(def.options || []).map(o => <option key={o.value} value={o.value}>{t(o.label_key || o.label)}</option>)}
        </Box>
      ) : def.type === "number" ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Box component="input" type="number" value={value ?? ""} disabled={disabled} placeholder="0"
            step={def.step ?? "any"} min={def.min} max={def.max}
            onChange={e => onChange(def.param_key, e.target.value)}
            sx={{ ...inputSx, width: PARAM_NUM_INPUT_W, flex: "0 0 auto" }} />
          {def.unit && (
            <Typography sx={{ fontSize: FONT.BODY, color: C.text3, flex: "0 0 auto" }}>{def.unit_key ? t(def.unit_key) : def.unit}</Typography>
          )}
        </Box>
      ) : def.type === "toggle" ? (
        /* 2택 세그먼트 — 체크박스는 클릭 표적이 작고 "무엇이 켜진 상태인지"가 라벨을 읽어야 보인다.
           목표재고 구성처럼 두 구성 중 하나를 고르는 값은 선택지를 나란히 놓는 편이 읽기 쉽다. */
        <AiSegmentedControl accent={accent} disabled={disabled}
          value={String(value).toUpperCase() === "Y" ? "Y" : "N"}
          options={[{ value: "N", label: t("ipTargetInvSafetyShort") },
                    { value: "Y", label: t("ipTargetInvCycleShort") }]}
          onChange={(v) => onChange(def.param_key, v)} sx={PARAM_TOGGLE_SX} />
      ) : def.type === "multiplier" ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Box component="input" type="number" step="0.1" min="0" value={value ?? ""} disabled={disabled}
            placeholder="1.0" onChange={e => onChange(def.param_key, e.target.value)}
            sx={{ ...inputSx, width: PARAM_NUM_INPUT_W, flex: "0 0 auto" }} />
          <Typography sx={{ fontSize: FONT.BODY, color: C.text3, flex: "0 0 auto" }}>{t("multiplierUnitLabel")}</Typography>
        </Box>
      ) : (
        <Box component="input" value={value ?? ""} disabled={disabled} placeholder="100%"
          onChange={e => onChange(def.param_key, e.target.value)} sx={inputSx} />
      )}
      {curLine && (
        <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, mt: 0.3 }}>
          {t("current")}: {curLine}
        </Typography>
      )}
    </Box>
    );
  };

  // ── 파라미터별 접기/펼치기(아코디언) ──────────────────────────────────────
  // **단일 펼침** — 편집 중인 파라미터 하나만 펼치고 나머지는 접는다(2026-08-06).
  //   조정 여부는 접힌 행의 "현행 A → B" 표시가 알려주므로 시드 값을 미리 펼칠 이유가 없다.
  const [openUid, setOpenUid] = useState(null);
  // 조정만 보기에서 "파라미터 추가"로 끌어온 행 — 아직 값이 없어 조정 판정에 안 걸리므로
  //   고정(pin)해 두지 않으면 추가하자마자 사라진다. uid 기준(대상 카드마다 독립).
  const [pinnedUids, setPinnedUids] = useState(() => new Set());
  // 적용 단위(scope) — overrides 파생이 아니라 상태로 보관. 카드 전체 해제(overrides=[])해도
  // "전체"로 되돌아가지 않아, 비웠다가 다른 대상을 다시 선택할 수 있다.
  const [scopeMode, setScopeModeState] = useState(() => scenario?.overrides?.[0]?.scope_type ?? "all");
  // 버전 전역 파라미터 값 — override 행과 독립 보관(카드 0개여도 값 유지, 새 행에 시드).
  const _pickVer = (sc) => {
    const p = sc?.overrides?.[0]?.params || {};
    const out = {};
    versionParamKeys(sc?.param_defs).forEach(k => { if (p[k] !== undefined) out[k] = p[k]; });
    return out;
  };
  const [verState, setVerState] = useState(() => _pickVer(scenario));
  useEffect(() => {
    setOpenUid(null);
    setPinnedUids(new Set());
    setScopeModeState(scenario?.overrides?.[0]?.scope_type ?? "all");
    setVerState(_pickVer(scenario));
    // 시나리오 인스턴스 교체 시 + 명시적 되돌리기(__resetAt) 시에만 재도출.
    // (편집 중 overrides 변경으로는 재도출하지 않아 펼침·scope·버전값을 보존한다.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario?.role, scenario?.scenario_cd, scenario?.__resetAt]);
  // 편집을 blur 로 끝낸 직후의 클릭은 무시 — 안 그러면 같은 행을 눌러 끝냈는데 도로 열린다.
  const editGuard = useEditEndGuard();
  const endEdit = () => { editGuard.markEnd(); setOpenUid(null); };
  const toggleOpen = (uid) => {
    if (editGuard.justEnded()) return;
    setOpenUid(cur => (cur === uid ? null : uid));
  };

  // ── 조정 건수 보고 + 뷰 필터 ──────────────────────────────────────────────
  // 조정 = 유효값이 실린 param_key(대상 카드가 몇 개든 종류로 센다). 카드 헤더 토글 라벨이 된다.
  const _statKeys = new Set();
  const _ovList = Array.isArray(scenario?.overrides) ? scenario.overrides : null;
  for (const o of (_ovList || [])) {
    for (const [k, v] of Object.entries(o?.params || {})) {
      if (hasEffectiveValue(null, v)) _statKeys.add(k);
    }
  }
  // override 행이 하나도 없어도 버전 전역 값은 살아 있다(verState 가 권위) — 따로 합친다.
  for (const [k, v] of Object.entries(verState || {})) {
    if (hasEffectiveValue(null, v)) _statKeys.add(k);
  }
  // overrides 키 자체가 없는 평탄화 카드(FP) — 값은 scenario.params 에 산다(아래 레거시 분기와 같은 소스).
  //   이 분기가 없으면 값이 몇 개 실려도 changed 가 **구조적으로 항상 0** 이라 "조정 0" 이 거짓이 된다.
  //   🔴 param_defs 를 돈다(params 를 돌지 않는다) — 평탄화는 렌더 불가 타입을 param_defs 에서만
  //      버리고 params 에는 남기므로, params 를 세면 _totalN 보다 커져 "조정 5 / 전체 3" 이 된다.
  if (_ovList === null) {
    for (const d of (scenario?.param_defs || [])) {
      if (hasEffectiveValue(d, scenario?.params?.[d.param_key])) _statKeys.add(d.param_key);
    }
  }
  const _changedN = _statKeys.size;
  const _totalN   = scenario?.param_defs?.length || 0;
  useEffect(() => {
    if (scenario?.role === "base") return;          // Base 는 잠금 목록이라 토글 대상이 아니다
    onStats?.({ changed: _changedN, total: _totalN });
    // onStats 는 부모가 매 렌더 새로 만드는 함수라 deps 에서 제외(넣으면 무한 루프).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_changedN, _totalN, scenario?.role]);

  // 조정만 보기에서 이 행을 노출할까 — 조정됐거나, 사용자가 방금 추가(pin)한 것.
  const isVisible = (uid, applied) => view === "all" || applied || pinnedUids.has(uid);
  const pinAndOpen = (uid) => {
    setPinnedUids(s => { const n = new Set(s); n.add(uid); return n; });
    setOpenUid(uid);
  };
  // 목록에서 안 보이는 후보 → 추가 메뉴 항목.
  const addCandidates = (defs, uidOf, appliedOf) => defs
    .filter(d => !isVisible(uidOf(d), appliedOf(d)))
    .map(d => ({ key: uidOf(d), label: t(d.label_key || d.label) }));

  // 컴팩트 헤더 아코디언 1개 — 헤더(라벨·"현행 A → B"·되돌리기·▾) + 펼침 시 본문 입력.
  // uid: 펼침 상태 키(scope=all → param_key, 카드 → `${cd}:${param_key}`).
  const accordionParam = (uid, def, value, onChange, currentText, last = false) => {
    const hlTs = highlights?.[`${scenario.role}:${def.param_key}`];
    const hot = hlTs != null;
    // hot 은 **강조 전용** — 펼침(편집) 상태에 OR 하지 않는다. OR 하면 하이라이트 해제 코드가
    //   없는 탓에 (a) blur(endEdit → setOpenUid(null))로도 입력창이 안 닫혀 영구 고착되고,
    //   (b) ParamValueCells 의 focus/select 가 Agent Chat 입력창의 포커스를 뺏는다.
    //   AI 변경 사실은 아래 paramRowSx(hot)·accent·"AI 변경" 칩이 접힌 채로 그대로 알려 준다.
    const open = openUid === uid;
    const applied = hasEffectiveValue(def, value);
    // 세그먼트 칩은 "+ 주기재고"(안전재고에 더한다는 관계)지만, 접힌 행은 그 자체로 읽혀야 하므로
    //   구성 전체를 쓴다 — "안전재고 + 주기재고".
    const toggleText = (v) =>
      t(String(v).toUpperCase() === "Y" ? "ipTargetInvWithCycleShort" : "ipTargetInvSafetyShort");
    const afterText = fmtParamValue(def, value, t, toggleText);
    // 단일 컨트롤 타입은 행 클릭 = 값 셀에서 바로 편집(펼침 본문 없음).
    const inline = isInlineEditable(def);
    const inlineEditing = inline && open && !disabled;
    return (
      // key 에 hlTs 를 섞지 않는다 — paramRowSx 의 hot 스타일에는 애니메이션이 없어 remount는 시각 효과가 0이고,
      // 같은 파라미터를 다시 바꿀 때 편집 effect 만 재실행시킨다.
      <Box key={uid}
        sx={paramRowSx({ accent, applied, hot, last })}>
        <Box onClick={() => !disabled && toggleOpen(uid)}
          sx={{ display: "flex", alignItems: "center", gap: 0.5, px: PARAM_ROW_PAD_X, py: "6px",
                height: PARAM_ROW_MIN_H, boxSizing: "border-box", overflow: "hidden",
                cursor: disabled ? "default" : "pointer", userSelect: "none",
                "&:hover": disabled ? {} : { bgcolor: `${accent}08` } }}>
          {/* 라벨은 폭을 고정한다 — grow 를 주면 편집 중 값 셀과 남는 공간을 나눠 가져 입력이 밀린다. */}
          <Typography noWrap sx={{ fontSize: FONT.BODY, fontWeight: 700, color: C.text1,
                                   flex: "0 0 36%", minWidth: 0 }}>
            {t(def.label_key || def.label)}
          </Typography>
          <ParamValueCells applied={applied ? afterText : null}
            current={currentText} currentPrefix={currentPrefixFor(def, applied, t)}
            accent={hot ? C.acc : accent}
            input={inlineEditing ? renderDef(def, value, onChange, false, null, true) : null}
            onEditEnd={endEdit} />
          {hot && (
            <Box sx={{ flex: "0 0 auto", height: 15, px: "6px", borderRadius: "999px",
                       bgcolor: C.acc, color: "#fff", fontSize: FONT.MICRO, fontWeight: 700,
                       display: "flex", alignItems: "center" }}>{t("aiChangedBadge")}</Box>
          )}
          {/* 값이 있으면 "되돌리기", 값 없이 추가만 한 행이면 "목록에서 빼기" — 같은 버튼이
              두 역할을 한다. 후자를 감추면 방금 추가한 행을 도로 뺄 방법이 없어진다. */}
          {!disabled && (
            <ParamRevertButton accent={accent} hidden={!applied && !pinnedUids.has(uid)}
              title={t(applied ? "revertToKeepCurrent" : "removeParamLabel")}
              onClick={() => {
                onChange(def.param_key, "");
                // 추가(pin)해서 끌어온 행이면 고정도 함께 푼다 — 안 그러면 값만 비고 행이 남아
                //   조정만 보기에서 다시 치울 방법이 없다.
                setPinnedUids(s => { const n = new Set(s); n.delete(uid); return n; });
                setOpenUid(cur => (cur === uid ? null : cur));
              }} />
          )}
          {/* 펼침 표시는 본문이 따로 있는 행에만. 인라인 행에서도 자리는 남긴다 —
              통째로 빼면 그 행만 우측 폭이 좁아져 값·현행 열이 다른 행과 어긋난다. */}
          <Box component="span" sx={{ flex: "0 0 auto", color: C.text3, fontSize: 11, lineHeight: 1,
                width: 11, textAlign: "center",
                ...(inline ? { visibility: "hidden" } : {}),
                transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }}>▾</Box>
        </Box>
        {open && !inline && (
          <Box sx={{ px: PARAM_ROW_PAD_X, pb: "8px", pt: "4px", borderTop: `0.5px dashed ${C.border}` }}>
            {renderDef(def, value, onChange, false, undefined, true)}
          </Box>
        )}
      </Box>
    );
  };

  // param_defs 없는 변형(레거시 직접입력 등)은 이 컴포넌트 대상 아님 — 호출측 폴백.
  if (!scenario?.param_defs?.length) return null;

  const overrides = Array.isArray(scenario.overrides) ? scenario.overrides : null;

  // overrides 미존재(구버전·FP 평탄화) 폴백 — 전역 params 직접 편집
  //   조정 건수를 위 _statKeys 가 같은 소스(scenario.params)로 세므로, 여기도 "조정만 보기"를
  //   존중해야 배지와 목록이 어긋나지 않는다. 예전에는 view 를 아예 안 읽어 **토글이 떠도 무동작**이었다.
  if (overrides === null) {
    const uidOf = (d) => d.param_key;
    const appliedOf = (d) => hasEffectiveValue(d, scenario.params?.[d.param_key]);
    const shown = scenario.param_defs.filter(d => isVisible(uidOf(d), appliedOf(d)));
    const cands = addCandidates(scenario.param_defs, uidOf, appliedOf);
    return (
      <Stack spacing={0.8}>
        {shown.map(def =>
          hotWrap(def.param_key,
            renderDef(def, scenario.params?.[def.param_key], onParamChange)))}
        {view === "changed" && !disabled && (
          <Box sx={{ mt: 0.6 }}>
            <ScenarioParamAddPicker items={cands} accent={accent} onPick={pinAndOpen} />
          </Box>
        )}
      </Stack>
    );
  }

  // base — 무변경 기준선 (집계 가능한 파라미터의 현재값 나열)
  if (scenario.role === "base") {
    return (
      <Box sx={{ p: "10px 10px", borderRadius: 1, bgcolor: C.bg3,
                 border: `0.5px dashed ${C.border}` }}>
        <Typography sx={{ fontSize: FONT.BODY, fontWeight: 700, color: C.text2, mb: 0.4 }}>
          {t("keepCurrentNoChange")}
        </Typography>
        {scenario.param_defs.filter(d => d.current_display).map(d => (
          <Typography key={d.param_key} sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>
            {t(d.label_key || d.label)} · {fmtBaseCardCurrent(d)}
          </Typography>
        ))}
      </Box>
    );
  }

  // 변경 대상 후보 (fetchTemplates 가 variant 에 주입한 problem_targets).
  // IP scope 옵션(거점/ABC-XYZ 분류/전체) 판별:
  //   1) problem_targets.inv_classes 배열 존재(표준 카탈로그 경로), 또는
  //   2) IP 전용 param_defs 키(serviceLevelDelta/includeCycleStock) 존재, 또는
  //   3) override scope_type=inv_class
  // problem_targets 전달이 누락/지연돼도 param_defs로 안정적으로 IP를 식별해
  // RP 옵션(거점/품목/전체)로 폴백되는 것을 막는다.
  const targets = scenario.problem_targets || {};
  const _IP_PARAM_KEYS = new Set(["serviceLevelDelta", "includeCycleStock"]);
  const isIp = Array.isArray(targets.inv_classes)
    || (scenario.param_defs || []).some(d => _IP_PARAM_KEYS.has(d.param_key))
    || (scenario.overrides || []).some(o => o.scope_type === "inv_class" || o.inv_class_cd);
  const OV_SCOPE_OPTIONS = isIp ? IP_SCOPE_OPTIONS : RP_SCOPE_OPTIONS;

  // scope 메타(SCOPE_DEF) 기반 후보 목록 추출 — {cd, name, reason}.
  const optionsFor = (scope) => {
    const meta = SCOPE_DEF[scope];
    if (!meta?.candKey) return [];
    return (targets[meta.candKey] || [])
      .map(t => ({ cd: t[meta.cdKey], name: meta.nameKey ? t[meta.nameKey] : null, reason: t.reason }))
      .filter(o => o.cd);
  };
  const currentFor = (scope) => {
    const meta = SCOPE_DEF[scope];
    if (!meta?.candKey) return {};
    return Object.fromEntries((targets[meta.candKey] || []).map(t => [t[meta.cdKey], t.current || {}]));
  };
  const reasonFor = (scope) =>
    Object.fromEntries(optionsFor(scope).map(o => [o.cd, o.reason ?? null]));
  // 대상 선정 근거 표시 — 서버는 ko 원문(reason)과 i18n 키(reason_key + reason_args)를 함께 준다.
  //   override 행은 여러 경로에서 reason 문자열만 복사되므로, 행에 키가 없으면 후보 목록의 같은 대상
  //   행에서 키를 찾아 선택 언어로 그린다. 둘 다 없으면 원문을 쓴다.
  //   서버 형태는 두 가지다: 단일 키(reason_key + reason_args) 또는 조각 목록(reason_i18n: [{key, args}]).
  const keyedReason = (row) => {
    if (!row) return "";
    if (row.reason_key) return fieldText(t, row, "reason");
    if (Array.isArray(row.reason_i18n) && row.reason_i18n.length) {
      return row.reason_i18n.map(p => fieldText(t, { reason_key: p.key, reason_args: p.args }, "reason"))
        .filter(Boolean).join(" · ");
    }
    return "";
  };
  const reasonText = (ov) => {
    if (!ov) return "";
    const own = keyedReason(ov);
    if (own) return own;
    const m = SCOPE_DEF[ov.scope_type];
    const cd = m?.cdKey ? ov[m.cdKey] : null;
    const cand = (cd && m?.candKey) ? (targets[m.candKey] || []).find(r => r[m.cdKey] === cd) : null;
    return keyedReason(cand) || ov.reason || "";
  };

  // 카드 리스트용 후보 원본 행(문제 카운트 포함) — IP scope(거점/ABC-XYZ).
  // 백엔드가 위험도 내림차순으로 정렬해 전달(문제 항목 상단, 정상 항목 하단).
  const candRows = (scope) => {
    const m = SCOPE_DEF[scope];
    if (!m?.candKey) return [];
    return (targets[m.candKey] || []).filter(t => t[m.cdKey]);
  };

  // IP 카드 소스 = candRows ∪ 후보에 없는 override 대상. AI suggest 응답의
  // problem_targets.inv_classes 가 비어도, suggest 가 만든 override(cd)는 항상
  // 카드로 노출한다(과거: candRows 만 순회해 8개 override가 사라지는 회귀).
  const ipCardRows = (scope) => {
    const m = SCOPE_DEF[scope];
    if (!m?.cdKey) return [];
    const rows = candRows(scope);
    const seen = new Set(rows.map(r => r[m.cdKey]));
    const extra = [];
    for (const o of overrides) {
      const cd = o[m.cdKey];
      if (!cd || seen.has(cd)) continue;
      seen.add(cd);                       // overrides 내 중복 cd 방어(중복 카드 방지)
      extra.push({ [m.cdKey]: cd, reason: o.reason });
    }
    return [...rows, ...extra];
  };

  const fmtCurrent = (def, cur) => {
    if (cur == null) return fmtBaselineCurrent(def);
    const v = cur[def.param_key];
    // 대상(거점/분류)별 현재값이 없는 파라미터 — 서비스 레벨은 증감이라 기준값 자체가 없고,
    //   목표재고 주기재고 반영은 대상마다 달라 하나로 못 줄인다. 전체 적용에서 열이 비는 것과
    //   같은 이유이므로 여기서도 비운다. (`?? 0` 으로 메우면 "현행 0%" 라는 거짓 정보가 된다)
    if (v == null || v === "") return null;
    if (def.type === "select") {
      const opt = (def.options || []).find(o => o.value === v);
      return opt ? t(opt.label_key || opt.label) : String(v);
    }
    if (def.type === "toggle") return t("appliedPctLabel", { pct: v });
    return `${v}${def.unit_key ? t(def.unit_key) : (def.unit || "")}`;
  };

  const defaultParams = () => Object.fromEntries(scenario.param_defs.map(d =>
    [d.param_key, d.type === "toggle" ? "Y"
      : d.type === "select" ? (d.options?.[0]?.value ?? "")
      : d.type === "number" ? "0"
      : d.type === "multiplier" ? "1" : "100%"]));
  const presetParams = () => (overrides[0]?.params ? { ...overrides[0].params } : defaultParams());

  // 현재 scope 메타 — cdKey/nameKey/candKey/unitLabel/icon.
  const meta = SCOPE_DEF[scopeMode] || SCOPE_DEF.all;

  // 한 override 행을 scope 메타 기준으로 구성 (loc_cd/item_cd/inv_class_cd 중 해당 cdKey 채움).
  const mkRow = (scope, cd, name, reason, params) => {
    const m = SCOPE_DEF[scope];
    // 버전 전역 값을 새 행에 항상 주입(카드 0개→재선택 시 유실 방지).
    const row = { scope_type: scope, loc_cd: null, item_cd: null, inv_class_cd: null, item_grp_cd: null,
                  loc_name: null, item_name: null, item_grp_name: null,
                  params: { ...params, ...verState }, reason: reason ?? null };
    if (m?.cdKey) row[m.cdKey] = cd ?? "";
    if (m?.nameKey) row[m.nameKey] = name;
    return row;
  };

  const setOverrides = (next) => onScenarioChange?.(sc => ({ ...sc, overrides: next }));
  const patchOv  = (i, patch) =>
    setOverrides(overrides.map((o, j) => (j === i ? { ...o, ...patch } : o)));
  // 빈 변형(사용자가 alt2 활성화) 첫 편집 — 해당 인덱스 override 행이 없으면 현재 scope 로 생성.
  const patchOvParam = (i, key, val) =>
    setOverrides(
      overrides[i]
        ? overrides.map((o, j) => (j === i ? { ...o, params: { ...o.params, [key]: val } } : o))
        : [...overrides, mkRow(scopeMode, null, null, null, { [key]: val })]
    );
  const removeOv = (i) => setOverrides(overrides.filter((_, j) => j !== i));

  // 카드 토글 — cd 기준 override 행 추가/제거 (IP 카드 리스트 전용).
  const ovIdxOf = (cd) => overrides.findIndex(o => o[meta.cdKey] === cd);
  const toggleCard = (row) => {
    const cd = row[meta.cdKey];
    const i = ovIdxOf(cd);
    if (i >= 0) { removeOv(i); return; }
    const base = overrides.length ? { ...overrides[overrides.length - 1].params } : presetParams();
    setOverrides([...overrides,
      mkRow(scopeMode, cd, meta.nameKey ? row[meta.nameKey] : null, row.reason, base)]);
  };

  const makeRow = (mode, params, usedCds) => {
    const m = SCOPE_DEF[mode];
    const cands = targets[m.candKey] || [];
    const used = usedCds ?? new Set();
    const next = cands.find(t => !used.has(t[m.cdKey]));
    return mkRow(mode, next?.[m.cdKey], m.nameKey ? next?.[m.nameKey] : null, next?.reason, params);
  };

  const setScopeMode = (mode) => {
    if (mode === scopeMode) return;
    setScopeModeState(mode);
    const base = presetParams();
    if (mode === "all") {
      setOverrides([mkRow("all", null, null, null, base)]);
      return;
    }
    const m = SCOPE_DEF[mode];
    const cands = targets[m.candKey] || [];
    const rows = (cands.length ? cands.slice(0, 1) : [null]).map(row =>
      mkRow(mode, row?.[m.cdKey], m.nameKey ? row?.[m.nameKey] : null, row?.reason, { ...base }));
    setOverrides(rows);
  };

  const candCount = optionsFor(scopeMode).length;
  const canAdd = overrides.length < candCount;

  const addOv = () => {
    if (!canAdd) return;
    const base = overrides.length ? { ...overrides[overrides.length - 1].params } : defaultParams();
    const used = new Set(overrides.map(o => o[meta.cdKey]));
    setOverrides([...overrides, makeRow(scopeMode, base, used)]);
  };

  const codeSelect = (value, options, placeholder, onChange) => (
    <Box component="select" value={value ?? ""} disabled={disabled}
      onChange={e => onChange(e.target.value)}
      sx={{ ...inputSx, cursor: disabled ? "not-allowed" : "pointer" }}>
      <option value="">{placeholder}</option>
      {value && !options.some(o => o.cd === value) && <option value={value}>{value}</option>}
      {options.map(o => (
        <option key={o.cd} value={o.cd}>
          {o.cd}{o.name ? ` (${o.name})` : ""}
        </option>
      ))}
    </Box>
  );

  const isAll = scopeMode === "all";
  const unitLabel = t(meta.unitLabel);

  // 버전 전역 파라미터(실적/수요계획 반영 구간)는 최상단 일괄 블록에서만 편집.
  // 대상(거점/분류)별 카드·그리드에는 그 외 파라미터만 노출.
  const verKeys     = versionParamKeys(scenario.param_defs);
  const versionDefs = scenario.param_defs.filter(d => verKeys.has(d.param_key));
  const targetDefs  = scenario.param_defs.filter(d => !verKeys.has(d.param_key));
  const verParams   = verState;
  // 버전 값은 verState(권위) + 모든 override 행에 동기화(백엔드가 행 머지로 추출 → 어느 행에서든 일관).
  const setVersionParam = (key, val) => {
    setVerState(s => ({ ...s, [key]: val }));
    setOverrides((Array.isArray(overrides) ? overrides : [])
      .map(o => ({ ...o, params: { ...o.params, [key]: val } })));
  };

  return (
    <Stack spacing={1}>
      {/* 버전 일괄 적용 — 실적/수요계획 반영 구간(분류 버전 전역) */}
      {(() => {
        const uidOf = (d) => d.param_key;
        const appliedOf = (d) => hasEffectiveValue(d, verParams[d.param_key]);
        const shown = versionDefs.filter(d => isVisible(uidOf(d), appliedOf(d)));
        const cands = addCandidates(versionDefs, uidOf, appliedOf);
        if (!versionDefs.length || (!shown.length && !cands.length)) return null;
        return (
        <Box>
          <Box sx={paramGroupHeaderSx}>
            <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text2, flex: "0 0 auto" }}>{t("versionBulkApplyTitle")}</Typography>
            <Typography sx={{ fontSize: FONT.MICRO, color: C.text3, flex: "0 0 auto" }}>{t("versionGlobalScopeNote")}</Typography>
            <Box sx={paramGroupRuleSx} />
          </Box>
          <Box>
            {shown.map((def, i) =>
              accordionParam(def.param_key, def, verParams[def.param_key], setVersionParam,
                fmtBaselineCurrent(def), i === shown.length - 1))}
          </Box>
          {view === "changed" && !disabled && (
            <Box sx={{ mt: 0.6 }}>
              <ScenarioParamAddPicker items={cands} accent={accent} onPick={pinAndOpen} />
            </Box>
          )}
        </Box>
        );
      })()}
      {/* 시나리오 단위 선택기 — 전체/거점/품목 세그먼트 토글 */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
        <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text3,
                          flex: "0 0 auto", letterSpacing: "0.02em" }}>{t("applyUnitLabel")}</Typography>
        <AiSegmentedControl accent={accent} disabled={disabled} value={scopeMode}
          options={OV_SCOPE_OPTIONS.map(o => ({ value: o.value, label: t(o.label) }))}
          onChange={setScopeMode} />
      </Box>

      {isAll ? (() => {
        const uidOf = (d) => d.param_key;
        const appliedOf = (d) => hasEffectiveValue(d, overrides[0]?.params?.[d.param_key]);
        const shown = targetDefs.filter(d => isVisible(uidOf(d), appliedOf(d)));
        const cands = addCandidates(targetDefs, uidOf, appliedOf);
        return (
        <Box>
          <Box sx={paramGroupHeaderSx}>
            <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text2, flex: "0 0 auto" }}>
              {isIp ? t("applyAllClassLabel") : t("applyAllItemLabel")}
            </Typography>
            <Box sx={paramGroupRuleSx} />
          </Box>
          <Box>
            {shown.map((def, i) =>
              accordionParam(def.param_key, def, overrides[0]?.params?.[def.param_key],
                (key, val) => patchOvParam(0, key, val), fmtBaselineCurrent(def),
                i === shown.length - 1))}
          </Box>
          {view === "changed" && !disabled && (
            <Box sx={{ mt: 0.6 }}>
              <ScenarioParamAddPicker items={cands} accent={accent} onPick={pinAndOpen} />
            </Box>
          )}
        </Box>
        );
      })() : isIp ? (() => {
        /* IP 카드 리스트 — 전체 거점/분면을 문제 우선 정렬로 노출, 카드 토글로 선택. */
        const cardRows = ipCardRows(scopeMode);   // 한 번만 계산 후 빈 체크·순회에 공용
        return (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
            <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text2 }}>
              {t("selectUnitLabel", { unit: unitLabel })}
            </Typography>
            <Box sx={{ minWidth: 16, height: 16, px: "5px", borderRadius: "8px",
                       bgcolor: `${accent}16`, color: accent, fontSize: FONT.MICRO, fontWeight: 700,
                       display: "flex", alignItems: "center", justifyContent: "center" }}>
              {overrides.length}
            </Box>
            <Typography sx={{ fontSize: FONT.MICRO, color: C.text3, ml: "auto" }}>
              {t("problemFirstSortLabel")}
            </Typography>
          </Box>
          {cardRows.length === 0 && (
            <Box sx={{ p: "12px 10px", borderRadius: PARAM_CARD_RADIUS, textAlign: "center",
                       border: `1px dashed ${C.border}`, bgcolor: C.bg3 }}>
              <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>
                {t("noSelectableUnitMsg", { unit: unitLabel })}
              </Typography>
            </Box>
          )}
          <Stack spacing={0.7}>
            {cardRows.map((row) => {
              const cd = row[meta.cdKey];
              const name = meta.nameKey ? row[meta.nameKey] : null;
              const ovIdx = ovIdxOf(cd);
              const selected = ovIdx >= 0;
              const short  = Number(row.critical_count) || 0;
              const excess = Number(row.excess_count) || 0;
              const ok = short === 0 && excess === 0;
              const sub = meta.value === "loc"
                ? (row.item_count != null ? t("itemCountSub", { count: Number(row.item_count).toLocaleString() }) : null)
                : (row.loc_count != null ? t("locCountSub", { count: Number(row.loc_count).toLocaleString() }) : null);
              const badge = (text, fg, bg) => (
                <Box sx={{ flex: "0 0 auto", height: 18, px: "6px", borderRadius: "9px",
                           display: "inline-flex", alignItems: "center",
                           bgcolor: bg, color: fg, fontSize: FONT.MICRO, fontWeight: 700 }}>
                  {text}
                </Box>
              );
              return (
                <Box key={cd}
                  sx={{ borderRadius: PARAM_CARD_RADIUS, bgcolor: C.bg2, overflow: "hidden",
                        border: `1px solid ${selected ? `${accent}55` : C.border}`,
                        boxShadow: selected ? `0 1px 3px ${accent}22` : "none",
                        transition: "border-color .12s, box-shadow .12s" }}>
                  <Box onClick={() => !disabled && toggleCard(row)}
                    sx={{ display: "flex", alignItems: "center", gap: 0.6, p: "8px 10px",
                          cursor: disabled ? "not-allowed" : "pointer",
                          "&:hover": disabled ? {} : { bgcolor: `${accent}08` } }}>
                    <Checkbox size="small" checked={selected} disabled={disabled} readOnly tabIndex={-1}
                      sx={{ p: 0, pointerEvents: "none", color: accent, "&.Mui-checked": { color: accent } }} />
                    <Box component="span" sx={{ flex: "0 0 auto", fontSize: 13 }}>{meta.icon}</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: FONT.BODY, fontWeight: 700, color: C.text1 }}>
                        {cd}{name ? ` (${name})` : ""}
                      </Typography>
                      {sub && (
                        <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>{sub}</Typography>
                      )}
                    </Box>
                    {short > 0 && badge(`${t("shortageBadge")} ${short.toLocaleString()}`, C.g3, C.g3l)}
                    {excess > 0 && badge(`${t("excessBadge")} ${excess.toLocaleString()}`, C.g2t, "#FBF0E2")}
                    {ok && badge(t("normalBadge"), C.text3, C.bg3)}
                  </Box>
                  {selected && (() => {
                    const ov = overrides[ovIdx];
                    const cur = currentFor(scopeMode)[cd];
                    return (
                      <Box sx={{ p: "0 10px 10px", borderTop: `1px dashed ${C.border}` }}>
                        {(() => {
                          const uidOf = (d) => `${cd}:${d.param_key}`;
                          const appliedOf = (d) => hasEffectiveValue(d, ov.params?.[d.param_key]);
                          const shown = targetDefs.filter(d => isVisible(uidOf(d), appliedOf(d)));
                          const cands = addCandidates(targetDefs, uidOf, appliedOf);
                          return (
                          <>
                            <Box sx={{ mt: 0.8 }}>
                              {shown.map((def, i) =>
                                accordionParam(uidOf(def), def, ov.params?.[def.param_key],
                                  (key, val) => patchOvParam(ovIdx, key, val), fmtCurrent(def, cur),
                                  i === shown.length - 1))}
                            </Box>
                            {view === "changed" && !disabled && (
                              <Box sx={{ mt: 0.6 }}>
                                <ScenarioParamAddPicker items={cands} accent={accent} onPick={pinAndOpen} />
                              </Box>
                            )}
                          </>
                          );
                        })()}
                        {ov.reason && (
                          <Box sx={{ display: "flex", gap: 0.5, alignItems: "baseline", mt: 0.7,
                                     pt: 0.6, borderTop: `1px dashed ${C.border}` }}>
                            <Typography sx={{ fontSize: FONT.MICRO, fontWeight: 700, color: C.text3, flex: "0 0 auto" }}>{t("reasonLabel")}</Typography>
                            <Typography sx={{ fontSize: FONT.CAPTION, color: C.g2t }}>{reasonText(ov)}</Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  })()}
                </Box>
              );
            })}
          </Stack>
        </>
        );
      })() : (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
            <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text2 }}>
              {t("changeTargetLabel")}
            </Typography>
            <Box sx={{ minWidth: 16, height: 16, px: "5px", borderRadius: "8px",
                       bgcolor: `${accent}16`, color: accent, fontSize: FONT.MICRO, fontWeight: 700,
                       display: "flex", alignItems: "center", justifyContent: "center" }}>
              {overrides.length}
            </Box>
          </Box>
          {overrides.length === 0 && (
            <Box sx={{ p: "12px 10px", borderRadius: PARAM_CARD_RADIUS, textAlign: "center",
                       border: `1px dashed ${C.border}`, bgcolor: C.bg3 }}>
              <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>
                {t("noChangeTargetMsg", { unit: unitLabel })}
              </Typography>
            </Box>
          )}
          {overrides.map((ov, i) => {
            const usedByOthers = new Set(
              overrides.filter((_, j) => j !== i)
                .map(o => o[meta.cdKey])
                .filter(Boolean));
            const rowOptions = optionsFor(scopeMode).filter(o => !usedByOthers.has(o.cd));
            return (
            <Box key={i}
              sx={{ p: "10px 11px", borderRadius: PARAM_CARD_RADIUS, bgcolor: C.bg2,
                    border: `1px solid ${accent}33`,
                    boxShadow: `0 1px 2px ${C.text1}08` }}>
              <Box sx={{ display: "flex", gap: 0.6, alignItems: "center", mb: 0.7 }}>
                <Box sx={{ flex: "0 0 auto", display: "inline-flex", alignItems: "center", gap: 0.4,
                           height: 22, px: "7px", borderRadius: "7px",
                           bgcolor: `${accent}12`, color: accent,
                           fontSize: FONT.MICRO, fontWeight: 700 }}>
                  <span>{meta.icon}</span>{unitLabel}
                </Box>
                <Box sx={{ flex: 1 }}>
                  {codeSelect(ov[meta.cdKey], rowOptions, t("selectUnitPlaceholder", { unit: unitLabel }),
                    v => patchOv(i, { [meta.cdKey]: v,
                                      ...(meta.nameKey ? { [meta.nameKey]: undefined } : {}),
                                      reason: reasonFor(scopeMode)[v] ?? null }))}
                </Box>
                <Box component="button" type="button" disabled={disabled}
                  onClick={() => removeOv(i)} title={t("removeTargetTitle")}
                  sx={{ flex: "0 0 auto", width: 22, height: 22, borderRadius: "6px", border: "none",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        bgcolor: "transparent", color: C.text3, cursor: disabled ? "not-allowed" : "pointer",
                        fontSize: FONT.BODY, lineHeight: 1, transition: "background-color .12s, color .12s",
                        "&:hover": { bgcolor: C.g3l, color: C.g3 } }}>✕</Box>
              </Box>
              <Box sx={{ display: "grid", gap: 0.6, gridTemplateColumns: "repeat(2, 1fr)" }}>
                {(() => {
                  const cur = currentFor(scopeMode)[ov[meta.cdKey]];
                  return scenario.param_defs.map(def => (
                    // 반영 구간(실적/수요계획)만 반 칸(좌우 2열), 그 외는 한 행 전체.
                    <Box key={def.param_key}
                      sx={{ gridColumn: HALF_WIDTH_PARAMS.has(def.param_key) ? "span 1" : "1 / -1" }}>
                      {renderDef(def, ov.params?.[def.param_key],
                        (key, val) => patchOvParam(i, key, val), false,
                        fmtCurrent(def, cur))}
                    </Box>
                  ));
                })()}
              </Box>
              {ov.reason && (
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "baseline", mt: 0.7,
                           pt: 0.6, borderTop: `1px dashed ${C.border}` }}>
                  <Typography sx={{ fontSize: FONT.MICRO, fontWeight: 700, color: C.text3, flex: "0 0 auto" }}>{t("reasonLabel")}</Typography>
                  <Typography sx={{ fontSize: FONT.CAPTION, color: C.g2t }}>{reasonText(ov)}</Typography>
                </Box>
              )}
            </Box>
            );
          })}
          <Box component="button" type="button" disabled={disabled || !canAdd} onClick={addOv}
            title={!canAdd ? t("allCandidatesAddedTitle", { unit: unitLabel, count: candCount }) : undefined}
            sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.4,
                  width: "100%", border: `1px dashed ${accent}70`, borderRadius: PARAM_CARD_RADIUS,
                  bgcolor: "transparent", color: accent, fontSize: FONT.CAPTION, fontWeight: 700,
                  fontFamily: "inherit", p: "7px 8px",
                  opacity: (disabled || !canAdd) ? 0.45 : 1,
                  cursor: (disabled || !canAdd) ? "not-allowed" : "pointer",
                  transition: "background-color .12s, border-color .12s",
                  "&:hover": (disabled || !canAdd) ? {} : { bgcolor: `${accent}0E`, borderColor: accent } }}>
            <Box component="span" sx={{ fontSize: FONT.SUB, lineHeight: 1 }}>＋</Box>
            {!canAdd ? t("allCandidatesAddedLabel", { unit: unitLabel, count: candCount }) : t("addUnitLabel", { unit: unitLabel })}
          </Box>
        </>
      )}
    </Stack>
  );
}
